import { useState, useEffect, useCallback } from 'react';
import { useUIStore } from '../../stores/uiStore';
import { api } from '../../api/client';
import {
  Loader2, Zap, Search, Globe, Filter, Hash, Play, Link2,
  AlertCircle, RefreshCw, CheckCircle, XCircle, TrendingUp,
  ChevronDown, ChevronUp, Package, ShieldCheck
} from 'lucide-react';

// ─── Constants ───
const SITE_OPTIONS = [
  { id: 'MLA', label: 'Argentina', flag: '\u{1F1E6}\u{1F1F7}' },
  { id: 'MLM', label: 'Mexico', flag: '\u{1F1F2}\u{1F1FD}' },
  { id: 'MLC', label: 'Chile', flag: '\u{1F1E8}\u{1F1F1}' },
  { id: 'MCO', label: 'Colombia', flag: '\u{1F1E8}\u{1F1F4}' },
  { id: 'MLU', label: 'Uruguay', flag: '\u{1F1FA}\u{1F1FE}' },
  { id: 'MLB', label: 'Brasil', flag: '\u{1F1E7}\u{1F1F7}' },
];

const INTERVAL_OPTIONS = [
  { value: 60, label: 'Cada 1 hora' },
  { value: 120, label: 'Cada 2 horas' },
  { value: 240, label: 'Cada 4 horas' },
  { value: 360, label: 'Cada 6 horas' },
  { value: 720, label: 'Cada 12 horas' },
  { value: 1440, label: 'Cada 24 horas' },
  { value: 4320, label: 'Cada 3 dias' },
  { value: 10080, label: 'Cada 7 dias' },
];

const SEARCH_METHOD_OPTIONS = [
  { value: 'gtin_first', label: 'GTIN primero, luego texto', help: 'Busca por codigo de barras (GTIN/EAN). Si no encuentra, busca por texto. Mejor precision.' },
  { value: 'text_only', label: 'Solo texto', help: 'Busca en Amazon solo por titulo del producto. Mas rapido pero menos preciso.' },
  { value: 'gtin_only', label: 'Solo GTIN', help: 'Solo busca por codigo de barras. Ignora productos sin GTIN. Maxima precision, menor cobertura.' },
];

const CONDITION_OPTIONS = [
  { value: '', label: 'Todos' },
  { value: 'new', label: 'Nuevo' },
  { value: 'refurbished', label: 'Refurbished' },
];

// ─── Styles ───
const inputCls = 'w-full px-3 py-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] text-[var(--color-text)] text-sm';
const labelCls = 'block text-xs font-medium text-[var(--color-text-secondary)] mb-1';
const helpCls = 'text-[10px] text-[var(--color-text-tertiary)] mt-0.5';
const sectionCls = 'rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] p-4';
const sectionTitleCls = 'text-sm font-semibold text-[var(--color-text)] flex items-center gap-2 cursor-pointer select-none';
const chipSelected = 'bg-[var(--color-primary)] text-white border-[var(--color-primary)]';
const chipUnselected = 'bg-[var(--color-surface)] text-[var(--color-text-secondary)] border-[var(--color-border)] hover:border-[var(--color-primary)]';

// ─── Collapsible Section ───
function Section({ icon: Icon, title, children, defaultOpen = true }: {
  icon: any; title: string; children: React.ReactNode; defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className={sectionCls}>
      <div className={sectionTitleCls} onClick={() => setOpen(!open)}>
        <Icon size={16} className="text-[var(--color-primary)]" />
        {title}
        <span className="ml-auto">{open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}</span>
      </div>
      {open && <div className="mt-3 space-y-3">{children}</div>}
    </div>
  );
}

// ─── Toast ───
function Toast({ msg, ok }: { msg: string; ok: boolean }) {
  return (
    <div className={`fixed bottom-4 right-4 z-50 px-4 py-2 rounded-lg text-sm text-white shadow-lg ${ok ? 'bg-green-600' : 'bg-red-600'}`}>
      {ok ? <CheckCircle size={14} className="inline mr-1.5 -mt-0.5" /> : <XCircle size={14} className="inline mr-1.5 -mt-0.5" />}
      {msg}
    </div>
  );
}

// ═══════════════════════════════════════════
// Main Tab Component
// ═══════════════════════════════════════════
export function TrendDiscoveryTab() {
  const { sellerId } = useUIStore();

  // ─── Scheduler config state ───
  const [config, setConfig] = useState<Record<string, any> | null>(null);
  const [edits, setEdits] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // ─── Feeder filters state ───
  const [filters, setFilters] = useState<Record<string, any>>({});
  const [filterEdits, setFilterEdits] = useState<Record<string, any>>({});
  const [filtersLoading, setFiltersLoading] = useState(true);
  const [filtersSaving, setFiltersSaving] = useState(false);

  // ─── History state ───
  const [history, setHistory] = useState<any[]>([]);
  const [historyLoading, setHistoryLoading] = useState(true);

  // ─── Process state ───
  const [running, setRunning] = useState(false);
  const [enabled, setEnabled] = useState(false);

  // ─── Toast ───
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);
  const showToast = (msg: string, ok: boolean) => {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 3000);
  };

  // ─── Load all data ───
  const loadConfig = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get<any>(`/scheduler/trends_discovery/settings?seller_id=${sellerId}`);
      if (res.success && res.data) {
        setConfig(res.data);
        setEdits(res.data);
      }
    } catch (err) {
      console.error('Failed to load trends config:', err);
    }
    setLoading(false);
  }, [sellerId]);

  const loadFilters = useCallback(async () => {
    setFiltersLoading(true);
    try {
      const res = await api.get<any>(`/publisher/filters?seller_id=${sellerId}`);
      if (res.success && res.data?.filters) {
        setFilters(res.data.filters);
        setFilterEdits(res.data.filters);
      }
    } catch (err) {
      console.error('Failed to load feeder filters:', err);
    }
    setFiltersLoading(false);
  }, [sellerId]);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const res = await api.get<any>(`/publisher/discover/trends/keywords?seller_id=${sellerId}&limit=20`);
      if (res.success && res.data) {
        setHistory(Array.isArray(res.data) ? res.data : res.data.keywords || []);
      }
    } catch {
      // Endpoint may not exist yet — non-critical
    }
    setHistoryLoading(false);
  }, [sellerId]);

  const loadProcessStatus = useCallback(async () => {
    try {
      const res = await api.get<any>(`/scheduler/status?seller_id=${sellerId}`);
      if (res.success && res.data) {
        const proc = res.data.processes?.find((p: any) => p.feature_key === 'trends_discovery');
        if (proc) {
          setEnabled(proc.enabled);
          setRunning(proc.running);
        }
      }
    } catch { /* non-critical */ }
  }, [sellerId]);

  useEffect(() => {
    loadConfig();
    loadFilters();
    loadHistory();
    loadProcessStatus();
  }, [loadConfig, loadFilters, loadHistory, loadProcessStatus]);

  // ─── Save scheduler config ───
  const handleSaveConfig = async () => {
    setSaving(true);
    try {
      const res = await api.put(`/scheduler/trends_discovery/settings?seller_id=${sellerId}`, { settings: edits });
      if (res.success) {
        setConfig(edits);
        showToast('Configuracion de tendencias guardada', true);
      } else {
        showToast((res as any).error || 'Error al guardar', false);
      }
    } catch {
      showToast('Error al guardar configuracion', false);
    }
    setSaving(false);
  };

  // ─── Save feeder filters ───
  const handleSaveFilters = async () => {
    setFiltersSaving(true);
    try {
      const res = await api.put('/publisher/filters', { seller_id: sellerId, filters: filterEdits });
      if (res.success) {
        setFilters(filterEdits);
        showToast('Filtros de productos guardados', true);
      } else {
        showToast((res as any).error || 'Error al guardar filtros', false);
      }
    } catch {
      showToast('Error al guardar filtros', false);
    }
    setFiltersSaving(false);
  };

  // ─── Toggle enabled ───
  const handleToggleEnabled = async () => {
    try {
      const res = await api.put(`/scheduler/trends_discovery/settings?seller_id=${sellerId}`, {
        settings: { enabled: !enabled },
      });
      if (res.success) {
        setEnabled(!enabled);
        showToast(enabled ? 'Descubrimiento desactivado' : 'Descubrimiento activado', true);
      }
    } catch {
      showToast('Error al cambiar estado', false);
    }
  };

  // ─── Run now ───
  const handleRunNow = async () => {
    setRunning(true);
    try {
      const res = await api.post(`/scheduler/trends_discovery/run?seller_id=${sellerId}`, {});
      if (res.success) {
        showToast('Descubrimiento iniciado', true);
      } else {
        showToast((res as any).error || 'Error al iniciar', false);
        setRunning(false);
      }
    } catch {
      showToast('Error al iniciar descubrimiento', false);
      setRunning(false);
    }
  };

  // ─── Helpers ───
  const update = (field: string, value: any) => setEdits(e => ({ ...e, [field]: value }));
  const updateFilter = (field: string, value: any) => setFilterEdits(e => ({ ...e, [field]: value }));

  const toggleSite = (siteId: string) => {
    const current: string[] = edits.sites || [];
    update('sites', current.includes(siteId) ? current.filter((s: string) => s !== siteId) : [...current, siteId]);
  };

  if (loading && filtersLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 size={24} className="animate-spin text-[var(--color-primary)]" />
      </div>
    );
  }

  const selectedMethod = SEARCH_METHOD_OPTIONS.find(o => o.value === (edits.search_method || 'gtin_first'));

  return (
    <div className="space-y-4">
      {/* Header with status + actions */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-[var(--color-text)]">Trend Discovery</h2>
          <p className="text-xs text-[var(--color-text-muted)]">
            Descubrimiento automatico de productos via tendencias de MercadoLibre
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleToggleEnabled}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
              enabled
                ? 'bg-green-500/10 text-green-500 border-green-500/30'
                : 'bg-[var(--color-bg)] text-[var(--color-text-muted)] border-[var(--color-border)]'
            }`}
          >
            {enabled ? 'Activo' : 'Inactivo'}
          </button>
          <button
            onClick={handleRunNow}
            disabled={running}
            className="px-3 py-1.5 rounded-lg text-xs font-medium border border-[var(--color-border)] text-[var(--color-text-secondary)] hover:bg-[var(--color-surface-hover)] disabled:opacity-50 flex items-center gap-1.5"
          >
            {running ? <Loader2 size={12} className="animate-spin" /> : <Zap size={12} />}
            Ejecutar ahora
          </button>
        </div>
      </div>

      {/* ═══ SCHEDULING ═══ */}
      <Section icon={Play} title="Programacion">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Frecuencia de ejecucion</label>
            <select
              value={edits.interval_minutes ?? 360}
              onChange={e => update('interval_minutes', parseInt(e.target.value))}
              className={inputCls}
            >
              {INTERVAL_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            <p className={helpCls}>Con que frecuencia buscar nuevas tendencias</p>
          </div>
          <div>
            <label className={labelCls}>Auto-encolar al enricher</label>
            <div className="flex items-center gap-3 mt-1">
              <button
                onClick={() => update('auto_queue', !edits.auto_queue)}
                className={`relative w-11 h-6 rounded-full transition-colors ${edits.auto_queue ? 'bg-green-500' : 'bg-gray-400'}`}
              >
                <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${edits.auto_queue ? 'translate-x-5' : 'translate-x-0.5'}`} />
              </button>
              <span className="text-xs text-[var(--color-text-secondary)]">
                {edits.auto_queue ? 'Los ASINs descubiertos se encolan automaticamente' : 'Los ASINs quedan como sugerencias'}
              </span>
            </div>
          </div>
        </div>
      </Section>

      {/* ═══ MARKETS ═══ */}
      <Section icon={Globe} title="Mercados">
        <div className="flex flex-wrap gap-2">
          {SITE_OPTIONS.map(site => {
            const selected = (edits.sites || []).includes(site.id);
            return (
              <button
                key={site.id}
                onClick={() => toggleSite(site.id)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${selected ? chipSelected : chipUnselected}`}
              >
                {site.flag} {site.id} — {site.label}
              </button>
            );
          })}
        </div>
        <p className={helpCls}>Paises de MercadoLibre donde buscar tendencias. Selecciona al menos uno.</p>
      </Section>

      {/* ═══ SEARCH SCOPE ═══ */}
      <Section icon={Search} title="Alcance de busqueda">
        <div className="space-y-3">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={edits.use_general_trends !== false}
              onChange={e => update('use_general_trends', e.target.checked)}
              className="rounded border-[var(--color-border)] accent-[var(--color-primary)]"
            />
            <div>
              <span className="text-sm text-[var(--color-text)]">Tendencias generales</span>
              <p className={helpCls}>Productos mas vendidos y populares del momento en cada mercado</p>
            </div>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={edits.use_category_trends !== false}
              onChange={e => update('use_category_trends', e.target.checked)}
              className="rounded border-[var(--color-border)] accent-[var(--color-primary)]"
            />
            <div>
              <span className="text-sm text-[var(--color-text)]">Tendencias por categoria</span>
              <p className={helpCls}>Analiza categorias especificas de cada mercado</p>
            </div>
          </label>
          {edits.use_category_trends !== false && (
            <div className="ml-6 max-w-xs">
              <label className={labelCls}>Categorias top por sitio</label>
              <select
                value={edits.top_categories_per_site ?? 5}
                onChange={e => update('top_categories_per_site', parseInt(e.target.value))}
                className={inputCls}
              >
                {[3, 5, 8, 10, 15, 20].map(n => (
                  <option key={n} value={n}>{n} categorias</option>
                ))}
              </select>
            </div>
          )}
        </div>
      </Section>

      {/* ═══ AMAZON LINKING ═══ */}
      <Section icon={Link2} title="Vinculacion Amazon">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Metodo de busqueda</label>
            <select
              value={edits.search_method ?? 'gtin_first'}
              onChange={e => update('search_method', e.target.value)}
              className={inputCls}
            >
              {SEARCH_METHOD_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            {selectedMethod && <p className={helpCls}>{selectedMethod.help}</p>}
          </div>
          <div>
            <label className={labelCls}>Max ASINs por keyword</label>
            <input
              type="number"
              min={1}
              max={20}
              value={edits.max_asins_per_keyword ?? 3}
              onChange={e => update('max_asins_per_keyword', parseInt(e.target.value) || 3)}
              className={inputCls}
            />
            <p className={helpCls}>Cuantos productos Amazon vincular por cada palabra clave</p>
          </div>
        </div>
      </Section>

      {/* ═══ LIMITS ═══ */}
      <Section icon={Hash} title="Limites por ejecucion">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <label className={labelCls}>Max keywords</label>
            <input
              type="number"
              min={1}
              max={500}
              value={edits.max_keywords_per_run ?? 50}
              onChange={e => update('max_keywords_per_run', parseInt(e.target.value) || 50)}
              className={inputCls}
            />
          </div>
          <div>
            <label className={labelCls}>Max ASINs</label>
            <input
              type="number"
              min={1}
              max={1000}
              value={edits.max_asins_per_run ?? 100}
              onChange={e => update('max_asins_per_run', parseInt(e.target.value) || 100)}
              className={inputCls}
            />
          </div>
          <div>
            <label className={labelCls}>Budget Keepa tokens</label>
            <input
              type="number"
              min={0}
              max={5000}
              value={edits.keepa_tokens_budget ?? 200}
              onChange={e => update('keepa_tokens_budget', parseInt(e.target.value) || 200)}
              className={inputCls}
            />
            <p className={helpCls}>Tokens Keepa max por corrida</p>
          </div>
          <div>
            <label className={labelCls}>Max competidores</label>
            <input
              type="number"
              min={1}
              max={50}
              value={edits.max_competitors ?? 5}
              onChange={e => update('max_competitors', parseInt(e.target.value) || 5)}
              className={inputCls}
            />
            <p className={helpCls}>Max ofertas FBA para considerar viable</p>
          </div>
        </div>
      </Section>

      {/* ═══ SAVE CONFIG BUTTON ═══ */}
      <div className="flex justify-end">
        <button
          onClick={handleSaveConfig}
          disabled={saving}
          className="px-5 py-2 text-sm rounded-lg bg-[var(--color-primary)] text-white hover:opacity-90 disabled:opacity-50 flex items-center gap-2"
        >
          {saving ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle size={14} />}
          Guardar configuracion
        </button>
      </div>

      {/* ═══════════════════════════════════════ */}
      {/* FEEDER FILTERS (Product quality gates) */}
      {/* ═══════════════════════════════════════ */}
      <div className="border-t border-[var(--color-border)] pt-4 mt-2">
        <h3 className="text-base font-semibold text-[var(--color-text)] mb-3 flex items-center gap-2">
          <ShieldCheck size={18} className="text-[var(--color-primary)]" />
          Filtros de producto (Keepa)
        </h3>
        <p className="text-xs text-[var(--color-text-muted)] mb-4">
          Solo se encolan productos que pasen estos filtros. Aplican a tendencias y al enricher en general.
        </p>
      </div>

      {filtersLoading ? (
        <div className="flex justify-center py-4"><Loader2 size={20} className="animate-spin text-[var(--color-primary)]" /></div>
      ) : (
        <>
          {/* Price filters */}
          <Section icon={Filter} title="Precios Amazon (USD)" defaultOpen={true}>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div>
                <label className={labelCls}>Precio min (New)</label>
                <input
                  type="number"
                  step="1"
                  min={0}
                  value={filterEdits.current_NEW_gte ?? ''}
                  onChange={e => updateFilter('current_NEW_gte', e.target.value === '' ? null : parseInt(e.target.value))}
                  className={inputCls}
                  placeholder="ej. 500"
                />
                <p className={helpCls}>Centavos USD. 500 = $5.00</p>
              </div>
              <div>
                <label className={labelCls}>Precio max (New)</label>
                <input
                  type="number"
                  step="1"
                  min={0}
                  value={filterEdits.current_NEW_lte ?? ''}
                  onChange={e => updateFilter('current_NEW_lte', e.target.value === '' ? null : parseInt(e.target.value))}
                  className={inputCls}
                  placeholder="ej. 20000"
                />
                <p className={helpCls}>Centavos USD. 20000 = $200.00</p>
              </div>
              <div>
                <label className={labelCls}>Precio min FBA</label>
                <input
                  type="number"
                  step="1"
                  min={0}
                  value={filterEdits.current_NEW_FBA_gte ?? ''}
                  onChange={e => updateFilter('current_NEW_FBA_gte', e.target.value === '' ? null : parseInt(e.target.value))}
                  className={inputCls}
                  placeholder="0"
                />
                <p className={helpCls}>0 = Debe haber oferta FBA</p>
              </div>
              <div>
                <label className={labelCls}>BSR max (Sales Rank)</label>
                <input
                  type="number"
                  min={0}
                  value={filterEdits.current_SALES_lte ?? ''}
                  onChange={e => updateFilter('current_SALES_lte', e.target.value === '' ? null : parseInt(e.target.value))}
                  className={inputCls}
                  placeholder="ej. 30000"
                />
              </div>
            </div>
          </Section>

          {/* Quality filters */}
          <Section icon={TrendingUp} title="Calidad y demanda">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div>
                <label className={labelCls}>Rating minimo</label>
                <input
                  type="number"
                  step="1"
                  min={0}
                  max={50}
                  value={filterEdits.minRating ?? ''}
                  onChange={e => updateFilter('minRating', e.target.value === '' ? null : parseInt(e.target.value))}
                  className={inputCls}
                  placeholder="ej. 35"
                />
                <p className={helpCls}>x10. 35 = 3.5 estrellas</p>
              </div>
              <div>
                <label className={labelCls}>BSR promedio 90d max</label>
                <input
                  type="number"
                  min={0}
                  value={filterEdits.avg90_SALES_lte ?? ''}
                  onChange={e => updateFilter('avg90_SALES_lte', e.target.value === '' ? null : parseInt(e.target.value))}
                  className={inputCls}
                  placeholder="ej. 50000"
                />
              </div>
              <div>
                <label className={labelCls}>Max ASINs por busqueda</label>
                <input
                  type="number"
                  min={1}
                  max={50}
                  value={filterEdits.maxAsins ?? ''}
                  onChange={e => updateFilter('maxAsins', e.target.value === '' ? null : parseInt(e.target.value))}
                  className={inputCls}
                  placeholder="ej. 10"
                />
              </div>
              <div>
                <label className={labelCls}>Condicion</label>
                <select
                  value={filterEdits.itemCondition ?? ''}
                  onChange={e => updateFilter('itemCondition', e.target.value || null)}
                  className={inputCls}
                >
                  {CONDITION_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </div>
          </Section>

          {/* Shipping / size filters */}
          <Section icon={Package} title="Envio y dimensiones" defaultOpen={false}>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <div>
                <label className={labelCls}>Peso max paquete (g)</label>
                <input
                  type="number"
                  min={0}
                  value={filterEdits.packageWeight_lte ?? ''}
                  onChange={e => updateFilter('packageWeight_lte', e.target.value === '' ? null : parseInt(e.target.value))}
                  className={inputCls}
                  placeholder="ej. 30000"
                />
                <p className={helpCls}>30000g = 30kg</p>
              </div>
              <div>
                <label className={labelCls}>Lado max paquete (mm)</label>
                <input
                  type="number"
                  min={0}
                  value={filterEdits.packageMaxSide_lte ?? ''}
                  onChange={e => updateFilter('packageMaxSide_lte', e.target.value === '' ? null : parseInt(e.target.value))}
                  className={inputCls}
                  placeholder="ej. 1000"
                />
                <p className={helpCls}>1000mm = 100cm</p>
              </div>
              <div>
                <label className={labelCls}>Dias max entrega</label>
                <input
                  type="number"
                  min={0}
                  max={90}
                  value={filterEdits.maxDeliveryDays ?? ''}
                  onChange={e => updateFilter('maxDeliveryDays', e.target.value === '' ? null : parseInt(e.target.value))}
                  className={inputCls}
                  placeholder="ej. 30"
                />
              </div>
            </div>
            <div className="mt-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={filterEdits.excludePreorders === true}
                  onChange={e => updateFilter('excludePreorders', e.target.checked)}
                  className="rounded border-[var(--color-border)] accent-[var(--color-primary)]"
                />
                <span className="text-sm text-[var(--color-text)]">Excluir pre-orders</span>
              </label>
            </div>
          </Section>

          {/* Advanced text filters */}
          <Section icon={AlertCircle} title="Filtros avanzados (texto)" defaultOpen={false}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Filtro de titulo (contiene)</label>
                <input
                  type="text"
                  value={filterEdits.adv_title ?? ''}
                  onChange={e => updateFilter('adv_title', e.target.value)}
                  className={inputCls}
                  placeholder="Dejar vacio para no filtrar"
                />
              </div>
              <div>
                <label className={labelCls}>Tipo de producto Keepa</label>
                <input
                  type="text"
                  value={filterEdits.adv_productType ?? ''}
                  onChange={e => updateFilter('adv_productType', e.target.value)}
                  className={inputCls}
                  placeholder="0 = todos"
                />
              </div>
            </div>
          </Section>

          {/* Save filters button */}
          <div className="flex justify-end">
            <button
              onClick={handleSaveFilters}
              disabled={filtersSaving}
              className="px-5 py-2 text-sm rounded-lg bg-[var(--color-primary)] text-white hover:opacity-90 disabled:opacity-50 flex items-center gap-2"
            >
              {filtersSaving ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle size={14} />}
              Guardar filtros
            </button>
          </div>
        </>
      )}

      {/* ═══════════════════════════════ */}
      {/* DISCOVERY HISTORY              */}
      {/* ═══════════════════════════════ */}
      <div className="border-t border-[var(--color-border)] pt-4 mt-2">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-base font-semibold text-[var(--color-text)] flex items-center gap-2">
            <TrendingUp size={18} className="text-[var(--color-primary)]" />
            Historial reciente
          </h3>
          <button
            onClick={loadHistory}
            className="p-1.5 rounded hover:bg-[var(--color-surface-hover)] text-[var(--color-text-muted)]"
          >
            <RefreshCw className={`w-4 h-4 ${historyLoading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {historyLoading ? (
          <div className="flex justify-center py-4"><Loader2 size={16} className="animate-spin" /></div>
        ) : history.length === 0 ? (
          <p className="text-sm text-[var(--color-text-muted)] text-center py-4">
            Sin descubrimientos recientes
          </p>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-[var(--color-border)]">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-[var(--color-bg)] border-b border-[var(--color-border)]">
                  <th className="px-3 py-2 text-left font-medium text-[var(--color-text-muted)]">Keyword</th>
                  <th className="px-3 py-2 text-left font-medium text-[var(--color-text-muted)]">Sitio</th>
                  <th className="px-3 py-2 text-center font-medium text-[var(--color-text-muted)]">ASINs</th>
                  <th className="px-3 py-2 text-center font-medium text-[var(--color-text-muted)]">Viables</th>
                  <th className="px-3 py-2 text-center font-medium text-[var(--color-text-muted)]">Encolados</th>
                  <th className="px-3 py-2 text-center font-medium text-[var(--color-text-muted)]">Status</th>
                  <th className="px-3 py-2 text-right font-medium text-[var(--color-text-muted)]">Fecha</th>
                </tr>
              </thead>
              <tbody>
                {history.map((h: any, i: number) => (
                  <tr key={h.id || i} className="border-b border-[var(--color-border)] hover:bg-[var(--color-surface-hover)]">
                    <td className="px-3 py-2 text-[var(--color-text)]">{h.keyword}</td>
                    <td className="px-3 py-2 text-[var(--color-text-secondary)]">{h.site_id}</td>
                    <td className="px-3 py-2 text-center">{h.asins_found ?? '-'}</td>
                    <td className="px-3 py-2 text-center text-green-500">{h.asins_viable ?? '-'}</td>
                    <td className="px-3 py-2 text-center text-blue-500">{h.asins_queued ?? '-'}</td>
                    <td className="px-3 py-2 text-center">
                      <span className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium ${
                        h.status === 'processed' ? 'bg-green-500/10 text-green-500' :
                        h.status === 'no_match' ? 'bg-yellow-500/10 text-yellow-500' :
                        'bg-red-500/10 text-red-500'
                      }`}>
                        {h.status}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right text-[var(--color-text-muted)]">
                      {h.created_at ? new Date(h.created_at).toLocaleDateString('es', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {toast && <Toast msg={toast.msg} ok={toast.ok} />}
    </div>
  );
}
